import { css } from '@emotion/react';
import { OnboardingBaseTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { useTranslation } from '@frontend/i18n';
import { type TaskContentProps, useOnboardingTaskProgress } from '@frontend/product-onboarding';
import { Text, TextLink } from '@frontend/design-system';
import { useBaseOnboardingProgress } from '../hooks/use-base-onboarding-progress';
import { useTaskActions } from '../task-actions/use-task-actions';
import { CompletionBanner } from './completion-banner';
import OnboardingChecklist from './onboarding-checklist';
import { ProgressBar } from './progress-bar';

const WEAVE_HELP_URL = 'https://www.weavehelp.com/weavehelp/article/call-intelligence-standard-and-pro-plans';

export function BaseProductTaskContent({ onClose, completedHotspots, flow }: TaskContentProps) {
  const { t } = useTranslation('call-intel-onboarding');

  const actions = useTaskActions({ onClose });
  const { completedGroups } = useBaseOnboardingProgress();

  const { tasks, completedCount, totalCount, progressPercent, isAllCompleted } = useOnboardingTaskProgress({
    flow,
    completedHotspots,
    completedGroups,
  });

  return (
    <OnboardingChecklist>
      <OnboardingChecklist.Header title={t('Get Started with Summaries & Transcripts')} onClose={onClose}>
        <Text size='small' color='light'>
          {t('{{completed}} of {{total}} Completed', { completed: completedCount, total: totalCount })}
        </Text>
      </OnboardingChecklist.Header>

      <OnboardingChecklist.Body>
        <ProgressBar progress={progressPercent} />
        {isAllCompleted && <CompletionBanner />}
        <OnboardingChecklist.TaskList>
          {tasks.map((task) => {
            const action = actions[task.id as OnboardingBaseTask];
            return (
              <OnboardingChecklist.Task
                key={task.id}
                completed={task.isCompleted}
                disabled={!action?.isEnabled}
                loading={action?.isLoading}
                onSelect={action?.onSelect}
                description={task.description ?? ''}
                label={task.label}
                trackingId={task.tracking?.navigationTrackingId}
              />
            );
          })}
        </OnboardingChecklist.TaskList>
        {WEAVE_HELP_URL && (
          <Text as='span' size='small' color='light'>
            {t('Have additional questions? Visit')}{' '}
            <TextLink href={WEAVE_HELP_URL} target='_blank' rel='noreferrer' css={styles.helpLink}>
              {t('Weave Help')}
            </TextLink>
            .
          </Text>
        )}
      </OnboardingChecklist.Body>
    </OnboardingChecklist>
  );
}

const styles = {
  helpLink: css`
    text-decoration: underline;
  `,
};
